import {VideoPlayer, VideoPlayerProps} from "@buildwitt/shapeshift-common/v1/videoPlayer";
import {useContext, useEffect, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {ReactPlayerProps} from "react-player";
import {forwardRef} from "react";
import {VideoPlayerRef} from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";
import {PlayerAPI} from "bitmovin-player/modules/bitmovinplayer-core";
import {INotificationSeverity, NotificationContext} from "@buildwitt/shapeshift-identity";
import {VideoProviders} from "@buildwitt/shapeshift-common/v2/videoPlayer/types";
import {OnProgressProps} from "react-player/base";
import {brightcovePlayerConfig} from "../../../constants";
import {Button, useEventCallback} from "@mui/material";
import {LocaleContext} from "@/contexts/localeContext";
import { IAnnouncement } from "@/types/announcement";

const useStyles = makeStyles()((theme) => ({
	videoPlayerBackdrop: {
		position: "relative",
		width: "100%",
		paddingBottom: "56.25%",
		height: 0,
		overflow: "hidden",
		background: "black",
		boxSizing: "border-box",
		left: 0,
		top: 0,
		right: "0px",
		[theme.breakpoints.down("sm")]: {
			height: "250px"
		},
		"#bitmovin-player": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			objectFit: "cover"
		}
	},
	videoPlayer: {
		zIndex: 0,
		background: "black",
		position: "absolute",
		opacity: 0,
		left: 0,
		transition: "opacity .5s ease-in",
		right: "0px",
		height: "100% !important"

	},
	videoPlayerSpacer: {
		height: "550px",
		width: "100%",
		[theme.breakpoints.down("sm")]: {
			height: "250px"
		}
	}
}));

interface IPropsCustom {
	announcement?: IAnnouncement,
	bitmovinPlayer?: PlayerAPI | null,
	componentOverrides?: {
		header?: JSX.Element
	}
}

export type IProps = Omit<ReactPlayerProps, "url"> & IPropsCustom;

export const AnnouncementVideoPlayer = forwardRef<VideoPlayerRef, IProps>(function AnnouncementVideoPlayer({componentOverrides, announcement, currentTime, onReady, bitmovinPlayer, ...props}, externalRef) {
	const {classes} = useStyles();
	const [videoReady, setVideoReady] = useState(false);
	const {notify} = useContext(NotificationContext);
	const internalRef = useRef<VideoPlayerRef>({});
	// brightcove player - max time that can be seeked to
	const bcMaxTime = useRef<number|null>(null)
	const [player, setPlayer] = useState();
	const [locale] = useContext(LocaleContext)

	const handleSaveProgress = useEventCallback((currentTime: number): void => {
		props.onProgress?.({playedSeconds: currentTime})
	})

	const handleAllowComplete = useEventCallback((): void => {
		if (!announcement?.complete) {
			props.onVideoFinished?.();
		}
	})

	const isAnnouncementComplete = useEventCallback((): boolean => {
		return announcement?.complete
	})

	const handleReady: VideoPlayerProps["onReady"] = useEventCallback((extPlayer) => {
		onReady?.();

		setPlayer(extPlayer);

		// react player init
		((ytPlayer) => {
			if (!ytPlayer || announcement.videoType !== "url") {
				return;
			}

			const internalYtPlayer = internalRef.current.react?.react?.getInternalPlayer?.()
			if (!internalYtPlayer) {
				return;
			}

			try {
				internalYtPlayer.addEventListener("onPlaybackRateChange", (event: MessageEvent) => {
					const newRate = event.data;
					if (newRate > 1.5) {
						internalYtPlayer.setPlaybackRate(1.5);
					}
				});
				return;
			} catch (e) {
				return;
			}
		})(internalRef.current.react?.react?.player);

		// brightcove player init
		((bcPlayer?: videojs.VideoJsPlayer) => {
			if (!bcPlayer?.player || announcement.videoType !== "brightcove") {
				return
			}

			const saveProgressThrottled = (() => {
				let lastCall = 0;

				return (currentTime: number) => {
					const now = Date.now();
					// saves every 5 seconds
					if (now - lastCall >= 5000) {
						lastCall = now;
						handleSaveProgress(currentTime)
					}
				};
			})();

			// inits variables and starts at currentTime if available
			bcMaxTime.current = currentTime ?? null;

			bcPlayer.on("timeupdate", (...args) => {
				const currentTime = bcPlayer.currentTime();
				let validCurrentTime = currentTime;

				// event triggers several times per second
				// when seeking it will jump
				// sets max jump interval - 1s should be safe enough
				const MAX_JUMP = 1;
				if (!isAnnouncementComplete() && currentTime > bcMaxTime.current + MAX_JUMP) {
					validCurrentTime = bcMaxTime.current;
					bcPlayer.currentTime(validCurrentTime);
					notify("", "Skipping is not permitted for incomplete announcements", INotificationSeverity.warning, 5000, false)
					return;
				}

				bcMaxTime.current = Math.max(validCurrentTime, bcMaxTime.current ?? 0);
				saveProgressThrottled(validCurrentTime)
				// completes the announcement
				if (((bcPlayer.duration() - currentTime)) < 10) {
					handleAllowComplete();
				}
			})

			bcPlayer.on("loadedmetadata", () => {
				bcPlayer.playbackRates([0.5, 0.75, 1, 1.25, 1.5]);
				const tracks = bcPlayer.audioTracks();
				const userLang = (locale?.locale ?? "").split("-")[0].toLowerCase();
				if (!tracks || !tracks?.length || !userLang) {
					return;
				}

				let notMatched = true;

				for (let i = 0; i < tracks.length; i++) {
					const track = tracks[i];
					const trackLang = (track.language ?? "").split("-")[0].toLowerCase();
					if (trackLang && trackLang === userLang && notMatched) {
						track.enabled = true;
						notMatched = false;
					} else {
						track.enabled = false;
					}
				}
			});

			let firstPlay = true;
			bcPlayer.on("play", (e) => {
				if(firstPlay)
					bcPlayer.currentTime(currentTime);
				firstPlay = false;
			})

			bcPlayer.on("loadedmetadata", (...args) => {
				// runs when video is loaded
				// possible init place
			})
		})(extPlayer);

		setVideoReady(true);
	})

	useEffect(() => {
		setVideoReady(false)
	}, [announcement?.videoUrl])

	//BITMOVIN PLAYER RESTORE CONTROL
	useEffect(() => {
		if (videoReady && announcement.videoType === "bitmovin" && currentTime && bitmovinPlayer) {
			bitmovinPlayer.seek(currentTime);
		}
	}, [currentTime, videoReady, bitmovinPlayer])

	//REACT PLAYER RESTORE CONTROL
	useEffect(() => {
		if (!internalRef.current.react || announcement.videoType !== "url") return;

		const playerInstance = internalRef.current.react.react.player;
		if (videoReady && playerInstance && currentTime) {
			playerInstance.seekTo(currentTime, "seconds");
			playerInstance.player.play()
		}
	}, [currentTime, videoReady])

	const generateVideoRef = function <T extends { [x: string]: NonNullable<T> }>(
		current: T,
		type: VideoProviders
	): void | { [x: string]: NonNullable<T>; } | null {
		if (type !== "loom") internalRef.current[type] = current;

		// Ensure that the return type is compatible with the expected return type.
		if (current && externalRef) {
			const refReturn = typeof externalRef === "function"
				? externalRef(current)
				: (externalRef.current = current);

			// Ensure refReturn is a valid NonNullable<T>
			if (refReturn != null) {
				return refReturn !== undefined && refReturn !== null ? {[type]: refReturn as NonNullable<T>} : null;
			}
		}

		return null;
	};

	//BITMOVIN PLAYER PLAYBACK CONTROL
	useEffect(() => {
		if (!bitmovinPlayer)
			return;

		const originalSeek = bitmovinPlayer?.seek;

		const newSeek = (time: number, issuer?: string): boolean => {
			if (!issuer || !announcement || !videoReady || !bitmovinPlayer || announcement.complete || currentTime === time) {
				return (originalSeek.apply(bitmovinPlayer, [time, issuer]));
			}

			const diff = time - bitmovinPlayer.getCurrentTime();
			if (diff < 0) {
				return (originalSeek.apply(bitmovinPlayer, [time, issuer]));
			}

			notify("", "Skipping is not permitted for incomplete announcements", INotificationSeverity.warning, 5000, false)
			return false;

		}
		bitmovinPlayer.seek = newSeek;


	}, [bitmovinPlayer, announcement?.complete, videoReady]);

	// //REACT PLAYER PLAYBACK CONTROL
	const prevPlayedSeconds = useRef<number>(0);
	useEffect(() => { prevPlayedSeconds.current = 0 }, [announcement?.videoUrl])
	const handleSeek = ({playedSeconds}: OnProgressProps): void => {
		if (!internalRef.current.react) return;

		const playerInstance = internalRef.current.react.react.player;

		if (!announcement || !videoReady) {
			return;
		}

		const diff = playedSeconds - (prevPlayedSeconds.current ?? 0);
		if (prevPlayedSeconds.current > 0 && diff > 2 && !announcement.complete) {
			notify("", "Skipping is not permitted for incomplete announcements", INotificationSeverity.warning, 5000, false)
			playerInstance.seekTo((prevPlayedSeconds.current), "seconds");
			playerInstance.player.play()
		}
		else
			prevPlayedSeconds.current = playedSeconds;

		if (((playerInstance.getDuration() - playedSeconds)) < 10) {
			props.onVideoFinished?.();
		}
		props.onProgress?.({playedSeconds})
	};

	if (!announcement?.videoUrl) return <></>

	return (
		<>
			<div className={classes.videoPlayerBackdrop}>
				{componentOverrides?.header}
				<VideoPlayer
					{...props}
					moduleVideoType={announcement.videoType}
					ref={announcement.videoType === "bitmovin" ? externalRef : (current => generateVideoRef<any>(current, "react"))} //> to do fix any - previously before vite was - typeof current
					onReady={handleReady}
					className={classes.videoPlayer}
					style={{opacity: videoReady ? 1 : 0}}
					url={announcement.videoUrl}
					controls={true}
					providerDetails={{bitmovin: {player: bitmovinPlayer}, brightcove: brightcovePlayerConfig}}
					onProgress={announcement.videoType === "url" ? handleSeek : undefined}
				/>
			</div>
		</>
	);
});